const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const

/** Title-case a raw subject-type slug for display, e.g. ``policy_doc_section``
 *  → ``Policy doc section``. Used as the last-resort entity label when a
 *  subject can't be resolved to a name. */
export function humanizeSubjectType(type: string): string {
  const words = type.replace(/_/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : type
}

/** House-style "dd MMM yyyy HH:mm", browser-independent (not toLocale*). */
export function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  const dd = String(d.getDate()).padStart(2, '0')
  const mon = MONTH_ABBR[d.getMonth()]
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd} ${mon} ${yyyy} ${hh}:${mi}`
}

/** Coarse relative time — "just now", "16 days ago", "2 months ago".
 *  Pair with `formatTimestamp` in a `title` for the exact value. `nowMs` is
 *  injectable for deterministic tests; defaults to the current clock. */
export function relativeTime(iso: string, nowMs: number = Date.now()): string {
  const diffMs = nowMs - new Date(iso).getTime()
  const sec = Math.round(diffMs / 1000)
  if (sec < 45) return 'just now'
  const units: Array<[number, string]> = [
    [60, 'minute'],
    [60, 'hour'],
    [24, 'day'],
    [30, 'month'],
    [12, 'year'],
  ]
  // minutes, then divide up the ladder.
  let value = sec / 60
  let unit = 'minute'
  for (let i = 1; i < units.length; i++) {
    const [factor, name] = units[i]!
    if (value < factor) break
    value = value / factor
    unit = name
  }
  const n = Math.round(value)
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`
}
